'use client';

import { useContext, useState, useEffect, useRef } from 'react';
import ShibLink from '../atoms/shibLink';
import ShibLogo from '../atoms/shibLogo';
import { faChevronDown, faWallet } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'next-intl';
import { NavbarRouting } from '../../constants';
import React from 'react';
import { ModalContext } from '../../contexts/modal-provider';
import { useAccount, useDisconnect, useSwitchChain } from 'wagmi';
import { useUserInfoContext } from '../../contexts/userinfo-context';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { NETWORK_ICON } from '../../constants/networks';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { useMutation } from '@tanstack/react-query';
const Navbar = () => {
  const t = useTranslations('Navbar');
  const [openProfile, setOpenProfile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChainSwitcherOpen, setIsChainSwitcherOpen] = useState(false);
  const { openModal } = useContext(ModalContext);
  const { address, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { nLands } = useUserInfoContext();
  const [isConnected, setIsConnected] = useState(false);
  const { openConnectModal } = useConnectModal();
  const { chains, switchChain } = useSwitchChain();

  const router = useRouter();
  const currentLocale = useLocale();
  const chainSwitcherRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const { mutate: switchNetwork } = useMutation({
    mutationFn: async (chainId: number) => {
      await switchChain({ chainId });
    },
    onSuccess: () => {
      window.location.reload();
    },
  });

  const onLogout = () => {
    disconnect();
  };

  useEffect(() => {
    const isUserConnected = async () => {
      setIsConnected(!!address);
    };
    isUserConnected();
  }, [address]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chainSwitcherRef.current && !chainSwitcherRef.current.contains(event.target as Node)) {
        setIsChainSwitcherOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isWindowsPlatform = () => {
    if (typeof window === 'undefined') return false;
    return window.navigator.platform.toLowerCase().includes('win');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setOpenProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSwitchNetwork = (chainId: number) => {
    switchNetwork(chainId);
    setIsChainSwitcherOpen(false);
  };

  const NetworkSwitcherItem = ({
    chainId,
    networkName,
  }: {
    chainId: number;
    networkName: string;
  }) => {
    if (!chainId || !networkName) return null;
    return (
      <li className="z-50 flex items-center justify-start">
        <button
          onClick={() => handleSwitchNetwork(chainId)}
          className="block flex cursor-pointer items-center gap-2 whitespace-nowrap px-4 py-2 text-sm dark:text-gray-400 dark:hover:text-orange-500"
        >
          <Image
            width={24}
            height={24}
            src={NETWORK_ICON[chainId as keyof typeof NETWORK_ICON]}
            alt="Network icon"
            className="w-5"
          />
          {networkName
            ? networkName === 'Puppynet Shibarium'
              ? 'Puppynet Network'
              : networkName + ' Network'
            : ''}
        </button>
      </li>
    );
  };

  return (
    <nav className="fixed left-0 top-0 z-10 mx-auto flex min-h-[80px] w-screen justify-center bg-[rgba(15,15,15,0.4)] px-[4em] py-[12px] text-white backdrop-blur-[9px]">
      <div className="flex w-full max-w-[1720px] flex-col items-stretch justify-between sm:py-1 lg:flex-row">
        <div className="mb-4 flex justify-start lg:mb-0">
          <ShibLogo className="h-[45px] w-auto" />
        </div>

        <div className={`mx-8 hidden items-center lg:flex lg:flex-1`} id="navbar-desktop">
          <ul className="flex w-full flex-row items-center justify-center space-x-8">
            {NavbarRouting.map(route => (
              <li key={route?.href} className="flex">
                <ShibLink
                  href={route?.href}
                  scroll={false}
                  target={route.target}
                  className="active whitespace-nowrap text-sm hover:text-orange-500 active:text-orange-500"
                  onClick={e => {
                    e.preventDefault(); // Prevent default link behavior
                    if (window.location.pathname === '/') {
                      // If on root route, scroll to view
                      window.scrollTo(0, 0);
                    }
                    // Navigate to the new route
                    if (route.href) {
                      router.push(`/${currentLocale}${route.href}`);
                    }
                  }}
                >
                  {t(`${route.label}` as any)}
                </ShibLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex w-full flex-wrap items-center justify-between gap-2 lg:w-auto lg:flex-nowrap lg:justify-end">
          <button
            id="ham-menu"
            data-collapse-toggle="navbar-sticky"
            type="button"
            className="order-first inline-flex w-10 items-center justify-center rounded-lg p-2 text-sm text-white lg:hidden"
            aria-controls="navbar-sticky"
            aria-expanded={isMenuOpen ? 'true' : 'false'}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="sr-only"> {t('open')}</span>
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 17 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>
          </button>
          {isConnected ? (
            <div className="flex flex-col items-center space-y-2 lg:flex-row lg:items-center lg:space-x-4 lg:space-y-0">
              <div>
                <div className="dark relative self-center">
                  <button
                    type="button"
                    className="flex items-center gap-2 self-center whitespace-nowrap rounded-2xl border border-[#F17E0E] px-3 py-1 text-sm"
                    id="user-menu-button"
                    aria-expanded="false"
                    data-dropdown-toggle="dropdown"
                    onClick={() => setIsChainSwitcherOpen(!isChainSwitcherOpen)}
                  >
                    <span className="sr-only">Open chain switcher menu</span>
                    <div className="flex items-center gap-2 text-orange-500">
                      <Image
                        width={24}
                        height={24}
                        src={
                          NETWORK_ICON[chainId as keyof typeof NETWORK_ICON] ||
                          '/images/networks/mainnet-network.jpg'
                        }
                        alt="Network icon"
                        className={`w-5 ${Number(chainId) === 109 || Number(chainId) == 157 ? '' : 'h-5 rounded-full'}`}
                      />
                      {(() => {
                        const name = chains.find(chain => chain.id === chainId)?.name || '';
                        return name === 'Puppynet Shibarium'
                          ? 'Puppynet Network'
                          : name + ' Network';
                      })()}
                    </div>
                    <FontAwesomeIcon className="text-neutral-100" icon={faChevronDown} />
                  </button>
                  <div
                    ref={chainSwitcherRef}
                    className={`${
                      isChainSwitcherOpen ? '' : 'hidden'
                    } absolute right-0.5 top-5 z-50 my-4 w-48 list-none divide-y divide-gray-100 rounded bg-white text-base shadow dark:divide-gray-700 dark:bg-gray-800`}
                  >
                    <ul
                      className="z-50 py-1 font-light text-gray-500 dark:text-gray-400"
                      aria-labelledby="dropdown"
                    >
                      {chains.length > 0 &&
                        chains.map(chain => (
                          <NetworkSwitcherItem
                            key={chain.id}
                            chainId={chain.id}
                            networkName={chains.find(c => c.id === chain.id)?.name || ''}
                          />
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <div className="dark relative self-center" ref={profileMenuRef}>
                  <button
                    type="button"
                    className="flex items-center gap-2 self-center whitespace-nowrap rounded-2xl border border-[#F17E0E] px-3 py-1 text-sm"
                    id="user-menu-button"
                    aria-expanded="false"
                    data-dropdown-toggle="dropdown"
                    onClick={() => setOpenProfile(!openProfile)}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="text-orange-500">
                      <FontAwesomeIcon className="mr-2 text-orange-500" icon={faWallet} />
                      {address && <span>{`0x..${address?.slice(-4)}`}</span>}
                    </div>
                    <span className="block">
                      {t('my-lands')} ({nLands})
                    </span>
                    <FontAwesomeIcon className="text-neutral-100" icon={faChevronDown} />
                  </button>
                  <div
                    className={`${
                      openProfile ? '' : 'hidden'
                    } absolute right-0 top-5 z-50 my-4 w-52 list-none divide-y divide-gray-100 rounded bg-white text-base shadow dark:divide-gray-700 dark:bg-gray-800`}
                  >
                    <ul
                      className="py-1 font-light text-gray-500 dark:text-gray-400"
                      aria-labelledby="dropdown"
                    >
                      <li>
                        <button
                          onClick={() => openModal({ type: 'LAND_MODAL' })}
                          className="block cursor-pointer px-4 py-2 text-sm dark:text-gray-400 dark:hover:text-orange-500"
                        >
                          {t('view-lands')}
                        </button>
                      </li>
                    </ul>
                    <ul
                      className="py-1 font-light text-gray-500 dark:text-gray-400"
                      aria-labelledby="dropdown"
                    >
                      <li>
                        <button
                          onClick={onLogout}
                          className="block cursor-pointer px-4 py-2 text-sm dark:hover:text-orange-500"
                        >
                          {t('signout')}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid flex-1 grid-cols-2 items-center justify-end gap-2 sm:flex lg:flex-none lg:flex-nowrap">
              {isWindowsPlatform() && (
                <div className="col-span-1">
                  <Link
                    href={`https://download.shibthemetaverse.io/shibportal-latest-setup.exe`}
                    className="flex h-10 w-full items-center justify-center rounded-full border border-[#F17E0E] bg-[#F17E0E] px-4 text-center text-xs font-medium text-white shadow-[0px_2px_4px_0_rgba(0,0,0,0.4)] backdrop-blur-[24px] hover:bg-orange-600 focus:outline-none sm:w-[130px] md:text-sm"
                  >
                    <Image
                      src="/images/WindowsIcon.png"
                      alt="download"
                      width={20}
                      height={20}
                      className="mr-2"
                    />
                    <div className="flex flex-col items-start justify-center">
                      <div className="whitespace-nowrap text-left font-sora text-[10px] font-normal leading-3">
                        {t('download')}
                      </div>
                      <div className="whitespace-nowrap text-left font-sora text-[10px] font-semibold leading-3">
                        Windows
                      </div>
                    </div>
                  </Link>
                </div>
              )}
              {/* <div className="col-span-1">
                <Link
                  href={"https://portal.shibthemetaverse.io/login?redirect=ps"}
                  className="flex h-10 w-full sm:w-[130px] items-center space-x-2 justify-center rounded-full border border-[#404040] bg-[#191B1E] px-4 text-center text-xs md:text-sm font-medium text-white hover:opacity-80 focus:outline-none shadow-[0px_2px_4px_0_rgba(0,0,0,0.4)] backdrop-blur-[24px]"
                >
                  <Image
                    src="/images/globe-alt.svg"
                    alt="download"
                    width={24}
                    height={24}
                  />
                  <div className="flex flex-col items-start">
                    <div className="font-sora text-[10px] leading-3 text-left">
                      {t('play-on')}
                    </div>
                    <div className="font-sora text-[10px] leading-3 text-left">
                      {t('browser')}
                    </div>
                  </div>
                </Link>
              </div> */}
              <div className="col-span-1">
                <Link
                  href={`/${currentLocale}/map`}
                  className="flex h-10 w-full items-center justify-center whitespace-nowrap rounded-full border border-[#404040] bg-black/50 px-4 text-center text-xs font-medium text-white shadow-[0px_2px_4px_0_rgba(0,0,0,0.4)] backdrop-blur-[24px] hover:bg-black focus:outline-none sm:w-[130px] md:text-sm"
                >
                  {t('buy-your-land')}
                </Link>
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={openConnectModal}
                  className="flex h-10 w-full items-center justify-center whitespace-nowrap rounded-full border border-[#404040] bg-black/50 px-4 text-center text-xs font-medium text-white shadow-[0px_2px_4px_0_rgba(0,0,0,0.4)] backdrop-blur-[24px] hover:bg-black focus:outline-none sm:w-[160px] md:text-sm"
                >
                  {t('connect')}
                  <span className="ml-2">→</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className={`${
            isMenuOpen ? 'flex' : 'hidden'
          } absolute left-0 top-[8rem] flex min-h-[200px] w-full items-center lg:hidden`}
          id="navbar-mobile"
          style={{ zIndex: 9999 }} // Ensure position is set
        >
          <ul className="mx-auto flex w-[95%] flex-col rounded-lg border bg-black px-8 py-4">
            {NavbarRouting.map(route => (
              <li key={route?.href} className="flex">
                <ShibLink
                  href={route?.href}
                  scroll={false}
                  className="active text-sm hover:text-orange-500 active:text-orange-500"
                  onClick={e => {
                    e.preventDefault(); // Prevent default link behavior
                    if (window.location.pathname === '/') {
                      // If on root route, scroll to view
                      window.scrollTo(0, 0);
                    }
                    // Navigate to the new route
                    if (route.href) {
                      router.push(`/${currentLocale}${route.href}`);
                    }
                  }}
                >
                  {t(`${route.label}` as any)}
                </ShibLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
